import React, { useState, useEffect } from 'react';
import { Crown, Settings, Calendar, CreditCard } from 'lucide-react';
import { getUserPlan, createPortalSession } from '../utils/stripe';
import { getDatabase, ref, onValue } from 'firebase/database';

const SubscriptionManager = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const db = getDatabase();
  const userPlan = getUserPlan(user);

  useEffect(() => {
    if (!user) return;

    // Listen for subscription changes
    const subscriptionRef = ref(db, `users/${user.uid}/subscription`);
    const unsubscribe = onValue(subscriptionRef, (snapshot) => {
      const subData = snapshot.val();
      setSubscription(subData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, db]);

  const handleManageSubscription = async () => {
    if (!subscription?.customerId) {
      console.error('No customer ID found');
      return;
    }

    setLoading(true);
    try {
      await createPortalSession(subscription.customerId);
    } catch (error) {
      console.error('Error opening subscription portal:', error);
      alert('Unable to open subscription management. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-manager">
        <div className="loading">Loading subscription info...</div>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      <div className="subscription-header">
        <div className="plan-info">
          <div className="plan-icon">
            {userPlan.id === 'free' ? '🐾' : <Crown size={24} />}
          </div>
          <div className="plan-details">
            <h3>{userPlan.name}</h3>
            <p className="plan-status">
              {userPlan.id === 'free' 
                ? 'Free plan • Upgrade for unlimited uploads'
                : `Active • Next billing: ${subscription?.nextBillingDate || 'Unknown'}`
              }
            </p>
          </div>
        </div>
        
        {userPlan.id !== 'free' && (
          <button 
            onClick={handleManageSubscription}
            className="manage-button"
            disabled={loading}
          >
            <Settings size={16} />
            Manage
          </button>
        )}
      </div>

      <div className="subscription-features">
        <h4>Your Plan Features:</h4>
        <div className="features-grid">
          {userPlan.features.map((feature, index) => (
            <div key={index} className="feature-item">
              <span className="feature-text">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {userPlan.id === 'free' && (
        <div className="upgrade-prompt">
          <div className="upgrade-benefits">
            <h4>🚀 Upgrade to Premium:</h4>
            <ul>
              <li>✨ Unlimited bean uploads (vs 3/day)</li>
              <li>📊 Advanced analytics for your beans</li>
              <li>🚫 Ad-free experience</li>
              <li>👑 Premium badge on your profile</li>
              <li>💖 Extra super likes daily</li>
            </ul>
          </div>
        </div>
      )}

      {subscription && subscription.status === 'past_due' && (
        <div className="subscription-warning">
          <div className="warning-content">
            <CreditCard size={20} />
            <div>
              <h4>Payment Issue</h4>
              <p>Your subscription payment failed. Please update your payment method to continue enjoying premium features.</p>
            </div>
          </div>
          <button onClick={handleManageSubscription} className="update-payment-button">
            Update Payment
          </button>
        </div>
      )}

      {subscription && subscription.cancelAtPeriodEnd && (
        <div className="subscription-notice">
          <div className="notice-content">
            <Calendar size={20} />
            <div>
              <h4>Subscription Ending</h4>
              <p>Your premium subscription will end on {subscription.periodEnd}. You can reactivate anytime.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;